import PublicLayout from "@/components/PublicLayout";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BookOpen, Cross, Sun } from "lucide-react";

export default function BibleTeachings() {
  const [commandments, setCommandments] = useState([]);
  const [teachings, setTeachings] = useState([]);
  const [verse, setVerse] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/bible/commandments").then((r) => setCommandments(r.data.commandments)),
      api.get("/bible/teachings").then((r) => setTeachings(r.data.teachings)),
      api.get("/bible/daily-verse").then((r) => setVerse(r.data)),
    ]).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      <section className="celestial-bg">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
          <p className="text-xs uppercase tracking-widest text-brand-quiet">Bible Teachings</p>
          <h1 className="mt-3 font-serif text-5xl sm:text-6xl tracking-tight text-brand-ink">
            The Word, gently unfolded.
          </h1>
          <p className="mt-4 text-lg text-brand-quiet max-w-2xl leading-relaxed">
            Sit with the commandments of God, the teachings of Jesus, and a verse for this very day.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-3 rounded-2xl bg-white border border-brand-line p-8 soft-shadow">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand-quiet">
            <Sun className="w-4 h-4 text-brand-gold" strokeWidth={1.6} /> Today’s verse
          </div>
          <p className="mt-4 scripture-quote text-3xl sm:text-4xl text-brand-ink leading-snug text-balance">
            “{verse?.text}”
          </p>
          <p className="text-sm text-brand-muted mt-3">— {verse?.reference}</p>
        </div>

        <div className="lg:col-span-2 rounded-2xl bg-white border border-brand-line p-8">
          <div className="flex items-center gap-2 mb-5">
            <Cross className="w-5 h-5 text-brand-sky" strokeWidth={1.6} />
            <h2 className="font-serif text-3xl text-brand-ink">The Ten Commandments</h2>
          </div>
          <ol className="space-y-3">
            {commandments.map((c) => (
              <li
                key={c.n}
                data-testid={`commandment-${c.n}`}
                className="flex gap-4 p-3 rounded-xl hover:bg-brand-base transition-colors"
              >
                <span className="shrink-0 w-8 h-8 rounded-full bg-brand-mist text-brand-skyDark grid place-items-center text-sm font-medium">
                  {c.n}
                </span>
                <div>
                  <p className="text-brand-ink leading-snug">{c.title}</p>
                  <p className="text-xs text-brand-muted mt-0.5">{c.reference}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-2xl bg-white border border-brand-line p-8">
          <div className="flex items-center gap-2 mb-5">
            <BookOpen className="w-5 h-5 text-brand-sage" strokeWidth={1.6} />
            <h2 className="font-serif text-3xl text-brand-ink">Teachings of Jesus</h2>
          </div>
          <div className="space-y-5">
            {teachings.map((t) => (
              <div key={t.reference} className="border-l-2 border-brand-mist pl-4">
                <p className="text-sm font-medium text-brand-ink">{t.title}</p>
                <p className="mt-1 scripture-quote text-base text-brand-quiet leading-snug">“{t.text}”</p>
                <p className="text-xs text-brand-muted mt-1">— {t.reference}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
